//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"
import {PanelData, PanelDirection, PanelType} from "./PanelData";
import {on, removeListener, sendMessageData} from "api/prokey/prokey.websocket";

//#endregion

const ContextLayout = createContext();

//-----------------------------------------------------------------
export function LayoutProvider( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { children } = props;

  const ref_auto_id = useRef(10);

  // Allows users to customize their layout without overriding saved layouts
  const layout_default_name  = props.name || 'Default';
  const layout_local_name    = 'Custom (local)';
  const layout_local_storage = props.name || 'file-viewer-layout';

  const [ force_render, setForceRender ] = useState(0);
  const [ layout_name , setLayoutName  ] = useState( layout_local_name );
  const [ layouts     , setLayouts     ] = useState( new Map() );
  
  const layout = useMemo( loadLayout, [layout_name, layouts] );

  const values = {
    layout_name,
    layouts,
    getAvailablePanels,
    setLayout,
    getRoot,
    getParent,
    getItem,
    moveItem,
    openItem,
    closeItem,
    createItem,
    removeItem,
    getSizes,
    setSizes,
    save,
    saveLayout,
    removeLayout,
    changeDirection,
    setType,
  };

  //#endregion

  //#region [EFFECT]

  useEffect( () => {

    function handleUserLayouts( data ) {
      if( !data?.layouts ) return;

      const layout_default = layout_default_name === 'layout-analytics' ? createLayoutAnalytics() : createDefaultLayout()

      const layout_map = new Map([
        [ layout_default_name , layout_default ],
        [ layout_local_name   , null ]
      ]);
      for( const name in data?.layouts ) {
        const preset = data?.layouts[name];
        layout_map.set( name, convertStringToLayout( preset ) );
      }

      setLayouts( layout_map );
    }

    on( 'USER_LAYOUTS', handleUserLayouts );

    sendMessageData( 'USER_LAYOUTS_REQUEST' );

    return () => {
      removeListener( 'USER_LAYOUTS', handleUserLayouts );
    }
  }, [] )

  useEffect( () => {
    // Save any changes to local layout when refreshing the page or disconnecting
    //-----------------------------------------------------------------
    function listener(event) {
      //-----------------------------------------------------------------
      save();
    }

    window.addEventListener('beforeunload', listener );

    return () => {
      window.removeEventListener('beforeunload', listener );
    }
  }, [layout] )

  //#endregion

  //#region [METHOD] === EXTERNAL

  //-----------------------------------------------------------------
  function setLayout( name ) {
    //-----------------------------------------------------------------
    if( layout_name === layout_local_name ) save();
    setLayoutName( name )
  }

  //-----------------------------------------------------------------
  function getRoot() {
    //-----------------------------------------------------------------
    return layout?.get(1);
  }

  //-----------------------------------------------------------------
  function getItem(id) {
    //-----------------------------------------------------------------
    return layout?.get( id );
  }

  //-----------------------------------------------------------------
  function moveItem( a, b, is_above ) {
  //-----------------------------------------------------------------
    const parent_a = getParent(a.id);
    const parent_b = getParent(b.id);

    const index_a = parent_a.items.indexOf( a.id );
    const index_b = parent_b.items.indexOf( b.id );

    const   to_above = is_above;
    const from_above = index_a < index_b;
    const to_above_from_above =  to_above &&  from_above;
    const to_above_from_below =  to_above && !from_above;
    const to_below_from_above = !to_above &&  from_above;
    const to_below_from_below = !to_above && !from_above;

    const direction = to_above_from_above ? 0 : to_above_from_below ? 0 : to_below_from_above ? 1 : to_below_from_below ? 1 : 0;

    const is_same_parent = parent_a.id === parent_b.id;
    if( is_same_parent ) {
      parent_a.removeItem( index_a );
      const index_b_new = from_above ? index_b-1 : index_b;
      parent_b.createItem( index_b_new + direction, a.id );
    } else {
      parent_a.removeItem( index_a );
      parent_b.createItem( index_b + direction, a.id );
    }

    _modify();
  }

  //-----------------------------------------------------------------
  function openItem( id ) {
    //-----------------------------------------------------------------
    if( !layout ) return;
    
    layout?.get( id )?.open();

    _modify();
  }

  //-----------------------------------------------------------------
  function closeItem( id ) {
    //-----------------------------------------------------------------
    if( !layout ) return;
    
    layout?.get( id )?.close();

    _modify();
  }

  //-----------------------------------------------------------------
  function createItem( parent_id, type ) {
    //-----------------------------------------------------------------
    if( !layout ) return;
    
    const new_panel = createDefaultPanel(type);
    layout?.set( new_panel.id, new_panel );
    const parent = getItem( parent_id );
    parent.items.push( new_panel.id );

    _modify();
  }

  //-----------------------------------------------------------------
  function removeItem( id ) {
    //-----------------------------------------------------------------
    if( !layout ) return;

    layout?.delete( id );
    const parent = getParent( id );
    const index = parent.items.indexOf( id );
    parent.items.splice( index, 1 );

    _modify();
  }

  //-----------------------------------------------------------------
  function changeDirection( id ) {
    //-----------------------------------------------------------------
    const item = getItem( id );
    item.changeDirection();

    _modify();
  }

  //-----------------------------------------------------------------
  function getSizes(id) {
    //-----------------------------------------------------------------
    const item = getItem(id);
    const temp = item.items.map( (c_id) => {
      const child = getItem(c_id);
      const value = child.is_open ? child.size : 0;
      return value;
    } )
           if( item.direction === PanelDirection.ROW ) {
      return { cols: [100] , rows: temp  };
    } else if( item.direction === PanelDirection.COL ) {
      return { cols: temp  , rows: [100] };
    }
  }
  
  //-----------------------------------------------------------------
  function setSizes( id, sizes ) {
    //-----------------------------------------------------------------
    const item = getItem(id);
    const values = sizes.cols.length>1 ? sizes.cols : sizes.rows;
    item.items.forEach( (c_id,index) => {
      const child = getItem(c_id);
      if( child.is_open ) child.setSize( values[index] );
    } );

    _modify();
  }

  //-----------------------------------------------------------------
  function setType( id, type ) {
    //-----------------------------------------------------------------
    const item = getItem( id );
    item.setType( type );

    _modify();
  }

  // Save changes locally; does not override named layouts
  //-----------------------------------------------------------------
  function save() {
    //-----------------------------------------------------------------
    if( !layout ) return;
    
    const value = convertLayoutToString( layout );
    if( value == undefined ) return;

    localStorage.setItem( layout_local_storage, value );
  }

  // Save changes to named layout; managed by server
  //-----------------------------------------------------------------
  function saveLayout( new_name ) {
    //-----------------------------------------------------------------
    if( new_name === layout_default_name || new_name === layout_local_name ) {
      return;
    }

    const value = convertLayoutToString( layout );
    sendMessageData( 'USER_LAYOUTS_CREATE', { name: new_name, layout: value } )
  }

  //-----------------------------------------------------------------
  function removeLayout( name ) {
    //-----------------------------------------------------------------
    if( name === layout_default_name || name === layout_local_name ) {
      return;
    }

    sendMessageData( 'USER_LAYOUTS_REMOVE', { name: name } );
  }

  //-----------------------------------------------------------------
  function getAvailablePanels() {
    //-----------------------------------------------------------------
    const panels = new Set( Object.values(PanelType) );
    for( let panel of layout.values() ) {
      panels.delete( panel.type );
    }
    panels.delete( PanelType.ANALYTICS_RESULT );
    panels.delete( PanelType.ANALYTICS_IMAGE );
    panels.delete( PanelType.ANALYTICS_THUMB );

    return [PanelType.PANEL, ...panels];
  }

  //#endregion

  //#region [METHOD] === INTERNAL

  //-----------------------------------------------------------------
  function getParent(id) {
    //-----------------------------------------------------------------
    if( !layout ) return;

    for( let panel of layout?.values() ) {
      if( panel.items.includes(id) ) {
        return panel;
      }
    }
  }

  //-----------------------------------------------------------------
  function createDefaultPanel( type ) {
    //-----------------------------------------------------------------
    const _panel = {
      id        : ref_auto_id.current++ ,
      type      : PanelType.PANEL ,
      size      : 50 ,
      open      : true ,
      direction : PanelDirection.ROW ,
    }

    const new_panel = new PanelData( _panel );
    new_panel.setType( type );

    return new_panel;
  }

  //-----------------------------------------------------------------
  function loadLayout() {
    //-----------------------------------------------------------------
    try {
      console.info('load layout:', layout_name);
    
      const layout_data = layouts.get( layout_name );
      if( layout_data == null && layout_name === layout_local_name ) {
        const  result = convertStringToLayout( localStorage.getItem( layout_local_storage ) );
        if( !result ) {
          setLayoutName( layout_default_name );
          return;
        }
        return result;
      } 
      else if( layout_data == null ) {
        setLayoutName( layout_default_name );
        return;
      };

      // return a copy
      const copy = convertStringToLayout( convertLayoutToString( layout_data ) );
      return copy;
    } catch (error) {
      console.warn( {error} );
    }
  }

  //-----------------------------------------------------------------
  function convertLayoutToString( layout_data ) {
    //-----------------------------------------------------------------
    try {
      if( !layout_data ) return;

      const temp = {}
      for( let panel of layout_data.values() ) {
        temp[panel.id] = panel.info();
      }

      const value = JSON.stringify( temp );

      return value;
    } catch (error) {
      console.warn( {error} );
    }
  }

  //-----------------------------------------------------------------
  function convertStringToLayout( layout_string ) {
    //-----------------------------------------------------------------
    try {
      if( !layout_string ) return '';
      const layout_data = JSON.parse( layout_string );

      const result = convertObjectToLayout( layout_data );
      return result;
    } catch (error) {
      console.warn( {error} );
    }
  }

  //-----------------------------------------------------------------
  function convertObjectToLayout( layout_data ) {
    //-----------------------------------------------------------------
    try {
      const result = new Map();
      for( let item of Object.values(layout_data) ) {
        result.set( item.id, new PanelData(item) );
      }

      const max_id = Math.max( ...result.keys() );
      ref_auto_id.current = max_id + 1;
      return result;
    } catch (error) {
      console.warn( {error} );
    }
  }

  //-----------------------------------------------------------------
  function _modify() {
    //-----------------------------------------------------------------
    try {
      const is_preset = layout_name !== layout_local_name;
      if( is_preset ) {
        layouts.set( layout_local_name, layout );
        setLayout( layout_local_name );
      }

      setForceRender( force_render+1 );
    } catch (error) {
      console.warn( {error} );
    }
  }

  //#endregion

  return (
    <ContextLayout.Provider value={ values }>
      {children}
    </ContextLayout.Provider>
  );
};

//-----------------------------------------------------------------
export function useLayout() {
  //-----------------------------------------------------------------
  return useContext( ContextLayout );
}

//-----------------------------------------------------------------
function createDefaultLayout() {
  //-----------------------------------------------------------------
  try {
    const _panel_1 = {
      id        : 1 ,
      type      : PanelType.ROOT ,
      size      : 100 ,
      open      : true ,
      direction : PanelDirection.COL ,
      is_locked : true,
      items     : [2,3] ,
    }
    const _panel_2 = {
      id        : 2 ,
      type      : PanelType.PANEL ,
      size      : 50 ,
      open      : true ,
      direction : PanelDirection.ROW ,
      // is_locked : true,
      items     : [4,5] ,
    }
    const _panel_3 = {
      id        : 3 ,
      type      : PanelType.PANEL ,
      size      : 50 ,
      open      : true ,
      direction : PanelDirection.COL ,
      // is_locked : true,
      items     : [6,7,8] ,
    }
    const _panel_4 = {
      id        : 4 ,
      type      : PanelType.DOCUMENTS ,
      size      : 50 ,
      open      : true ,
    }
    const _panel_5 = {
      id        : 5 ,
      type      : PanelType.COMMENTS ,
      size      : 50 ,
      open      : true ,
    }
    const _panel_6 = {
      id        : 6 ,
      type      : PanelType.REVIEW ,
      size      : 40 ,
      open      : true ,
    }
    const _panel_7 = {
      id        : 7 ,
      type      : PanelType.THUMB ,
      size      : 20 ,
      open      : true ,
    }
    const _panel_8 = {
      id        : 8 ,
      type      : PanelType.IMAGE ,
      size      : 40 ,
      open      : true ,
    }
    const _default_panel_1 = new PanelData(_panel_1);
    const _default_panel_2 = new PanelData(_panel_2);
    const _default_panel_3 = new PanelData(_panel_3);
    const _default_panel_4 = new PanelData(_panel_4);
    const _default_panel_5 = new PanelData(_panel_5);
    const _default_panel_6 = new PanelData(_panel_6);
    const _default_panel_7 = new PanelData(_panel_7);
    const _default_panel_8 = new PanelData(_panel_8);
    const _default_layout = new Map();
    _default_layout.set( _default_panel_1.id , _default_panel_1 );
    _default_layout.set( _default_panel_2.id , _default_panel_2 );
    _default_layout.set( _default_panel_3.id , _default_panel_3 );
    _default_layout.set( _default_panel_4.id , _default_panel_4 );
    _default_layout.set( _default_panel_5.id , _default_panel_5 );
    _default_layout.set( _default_panel_6.id , _default_panel_6 );
    _default_layout.set( _default_panel_7.id , _default_panel_7 );
    _default_layout.set( _default_panel_8.id , _default_panel_8 );

    return _default_layout;
  } catch (error) {
    console.warn( {error} );
  }
}

//-----------------------------------------------------------------
function createLayoutAnalytics() {
  //-----------------------------------------------------------------
  try {
    const _panel_1 = {
      id        : 1 ,
      type      : PanelType.ROOT ,
      size      : 100 ,
      open      : true ,
      direction : PanelDirection.COL ,
      is_locked : true,
      items     : [2,3,4] ,
    }
    const _panel_2 = {
      id        : 2 ,
      type      : PanelType.ANALYTICS_RESULT ,
      size      : 40 ,
      open      : true ,
    }
    const _panel_3 = {
      id        : 3 ,
      type      : PanelType.ANALYTICS_IMAGE ,
      size      : 40 ,
      open      : true ,
    }
    const _panel_4 = {
      id        : 4 ,
      type      : PanelType.ANALYTICS_THUMB ,
      size      : 20 ,
      open      : true ,
    }
    const _default_panel_1 = new PanelData(_panel_1);
    const _default_panel_2 = new PanelData(_panel_2);
    const _default_panel_3 = new PanelData(_panel_3);
    const _default_panel_4 = new PanelData(_panel_4);
    const _default_layout = new Map();
    _default_layout.set( _default_panel_1.id , _default_panel_1 );
    _default_layout.set( _default_panel_2.id , _default_panel_2 );
    _default_layout.set( _default_panel_3.id , _default_panel_3 );
    _default_layout.set( _default_panel_4.id , _default_panel_4 );

    return _default_layout;
  } catch (error) {
    console.warn( {error} );
  }
}